import Button from "../../components/ui/Button";
import React, { useEffect, useState } from "react";
import { getPaymentDetails, getReferralConfig, PaymentDetails, savePaymentDetails, saveReferralConfig } from "../../services/financeService";
import { useAccess } from "../../hooks/useAccess";
import { getApiErrorMessage } from "../../utils/apiError";

export function ReferralSettingsPanel() {
  const { can } = useAccess();
  const [form, setForm] = useState({ points: "", conversionNGN: "", conversionGBP: "" });
  const [loading, setLoading] = useState(true);
  const [exists, setExists] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [version, setVersion] = useState(0);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false; setLoading(true); setError(""); setLoaded(false);
    getReferralConfig().then(config => {
      if (cancelled) return; setExists(!!config); setLoaded(true);
      if (config) setForm({ points: String(config.points), conversionNGN: String(config.pointsConversionNGN), conversionGBP: String(config.pointsConversionGBP) });
    }).catch(err => { if (!cancelled) setError(getApiErrorMessage(err)); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [version]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setError(""); setMessage("");
    const body = { points: Number(form.points), conversionNGN: Number(form.conversionNGN), conversionGBP: Number(form.conversionGBP) };
    if (Object.values(body).some(value => !Number.isFinite(value) || value < 0)) { setError("Enter valid non-negative values."); return; }
    setBusy(true);
    try { await saveReferralConfig(body); setExists(true); setMessage("Referral configuration saved."); }
    catch (err) { setError(getApiErrorMessage(err)); }
    finally { setBusy(false); }
  };
  return <section className="integration-panel"><h2>Referral configuration</h2>
    {error && <p role="alert" className="integration-error">{error} <Button onClick={() => setVersion(value => value + 1)}>Reload</Button></p>}
    {message && <p role="status" className="integration-success">{message}</p>}
    {loading ? <p role="status">Loading referral configuration...</p> : loaded && <>
      {!exists && <p>No referral configuration has been set.</p>}
      <form className="integration-form" onSubmit={submit}>{([['points', 'Referral points'], ['conversionNGN', 'NGN conversion'], ['conversionGBP', 'GBP conversion']] as const).map(([key, label]) => <label key={key}>{label}<input type="number" min="0" step="any" required value={form[key]} disabled={busy || !can("referrals.manage")} onChange={event => setForm(previous => ({ ...previous, [key]: event.target.value }))} /></label>)}{can("referrals.manage") && <button className="primary" disabled={busy}>Save referral configuration</button>}</form>
    </>}
  </section>;
}

export function PaymentSettingsPanel() {
  const [form, setForm] = useState<PaymentDetails>({ bankName: "", accountName: "", accountNumber: "", sortCode: "", currency: "" });
  const [loading, setLoading] = useState(true);
  const [exists, setExists] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [version, setVersion] = useState(0);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false; setLoading(true); setError(""); setLoaded(false);
    getPaymentDetails().then(details => { if (!cancelled) { setExists(!!details); setLoaded(true); if (details) setForm(details); } }).catch(err => { if (!cancelled) setError(getApiErrorMessage(err)); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [version]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError(""); setMessage("");
    const body = Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value.trim()])) as unknown as PaymentDetails;
    try { await savePaymentDetails(body, exists); setExists(true); setMessage("Payment details saved."); }
    catch (err) { setError(getApiErrorMessage(err)); }
    finally { setBusy(false); }
  };
  return <section className="integration-panel"><h2>Payment details</h2>
    {error && <p role="alert" className="integration-error">{error} <Button onClick={() => setVersion(value => value + 1)}>Reload</Button></p>}
    {message && <p role="status" className="integration-success">{message}</p>}
    {loading ? <p role="status">Loading payment details...</p> : loaded && <form className="integration-form" onSubmit={submit}>
      {!exists && <p>No payment details have been set.</p>}
      {([['bankName', 'Bank name'], ['accountName', 'Account name'], ['accountNumber', 'Account number'], ['sortCode', 'Sort code'], ['currency', 'Currency']] as const).map(([key, label]) => <label key={key}>{label}<input value={form[key]} disabled={busy} required onChange={event => setForm(previous => ({ ...previous, [key]: event.target.value }))} /></label>)}
      <button className="primary" disabled={busy}>{exists ? "Update payment details" : "Create payment details"}</button>
    </form>}
  </section>;
}
